import { useState, useEffect, useRef } from "react";

// MUI
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
// Material Icons has no "whatsapp" glyph (it rendered blank); the SVG icon set does.
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Badge from "@mui/material/Badge";
import Checkbox from "@mui/material/Checkbox";
import FormGroup from "@mui/material/FormGroup";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

// MD components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDAlert from "components/MDAlert";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import RichTextEditor from "components/RichTextEditor";
import { Quill } from "react-quill";

import { adminApi } from "services/api";
import { BOOKING_STATUS_OPTIONS } from "utils/bookingStatuses";
import { removeStep, nextStepOrder, mergeStepAttachments, attachmentSizeError } from "./steps";
import { insertIntoRichText } from "./insertPlaceholder";

// ─── Source config ────────────────────────────────────────────────────────────
const SOURCES = [
  {
    value: "landing_application",
    label: "Landing Page Application",
    icon: "assignment",
    color: "success",
  },
  { value: "contact_submission", label: "Contact Form", icon: "contact_mail", color: "info" },
  {
    value: "bootcamp_application",
    label: "Bootcamp Application",
    icon: "school",
    color: "warning",
  },
  { value: "consultation", label: "Consultation Request", icon: "calendar_today", color: "error" },
  {
    value: "webinar_registration",
    label: "Webinar Registration",
    icon: "live_tv",
    color: "primary",
  },
  {
    value: "clarity_call_booking",
    label: "Clarity Call Booking",
    icon: "support_agent",
    color: "secondary",
  },
];

const SOURCE_MAP = Object.fromEntries(SOURCES.map((s) => [s.value, s]));

// ─── Course config ────────────────────────────────────────────────────────────
const COURSES = [
  { value: "data-ai", label: "Data & AI" },
  { value: "software-engineering", label: "Software Engineering" },
  { value: "cloud-network-infrastructure", label: "Cloud & Network Infrastructure" },
  { value: "cyber-security", label: "Cyber Security" },
  { value: "general", label: "General" },
];

const COURSE_MAP = Object.fromEntries(COURSES.map((c) => [c.value, c]));

const EMPTY_AUDIENCE = {
  registrationStages: [],
  callBooked: "any",
  registeredAfter: null,
  bookingStatuses: [],
};

// ─── Phone-only style helpers (below md — desktop keeps its current look) ─────
// Long chip labels wrap instead of being cut off with an ellipsis.
const phoneChipSx = (minHeight) => (theme) => ({
  [theme.breakpoints.down("md")]: {
    height: "auto",
    minHeight,
    "& .MuiChip-label": { whiteSpace: "normal", overflowWrap: "anywhere", py: "2px" },
  },
});

// Small IconButtons are 30px; grow them to a 36px tap target on phones (5px is MUI's small padding).
const actionTapSx = { p: { xs: 1, md: "5px" } };

// ─── Variable helper chips ────────────────────────────────────────────────────
const VARIABLES = [
  // universal
  { v: "name", note: "all" },
  { v: "first_name", note: "all" },
  { v: "email", note: "all" },
  { v: "phone", note: "all" },
  { v: "course", note: "all — course name" },
  // landing application
  { v: "region", note: "landing" },
  { v: "city", note: "landing" },
  { v: "ecosystem", note: "landing" },
  { v: "paymentPreference", note: "landing" },
  // consultation
  { v: "service", note: "consultation" },
  { v: "company", note: "consultation" },
  { v: "preferredDate", note: "consultation" },
  { v: "preferredTime", note: "consultation" },
  // clarity call booking
  { v: "callDate", note: "clarity call — booked call date" },
  { v: "callTime", note: "clarity call — booked call time" },
  { v: "manageLink", note: "clarity call — visitor's reschedule/cancel link" },
  // contact form
  { v: "subject", note: "contact" },
  // autoresponder settings
  { v: "bookingLink", note: "all" },
  { v: "whatsappLink", note: "all" },
  { v: "cohortDate", note: "all" },
  { v: "spotsRemaining", note: "all" },
  { v: "repName", note: "all" },
  { v: "currency", note: "all (region-aware)" },
  // webinar settings
  { v: "zoomUrl", note: "webinar — Zoom join link" },
  { v: "zoomId", note: "webinar — Meeting ID" },
  { v: "zoomPasscode", note: "webinar — Passcode" },
  { v: "webinarDate", note: "webinar — Training date" },
  { v: "webinarTime", note: "webinar — Training time" },
  { v: "webinarDay", note: "webinar — Weekday, e.g. Sunday" },
  { v: "webinarWhatsapp", note: "webinar — WhatsApp group link" },
  // file library — use file:slug format
  { v: "file:elite247-academy-brochure-2026", note: "→ brochure download link" },
];

function VariableChips({ onInsert }) {
  return (
    <MDBox display="flex" flexWrap="wrap" gap={{ xs: 0.75, md: 0.5 }} mb={1}>
      <MDTypography variant="caption" color="secondary" sx={{ alignSelf: "center", mr: 0.5 }}>
        Insert:
      </MDTypography>
      {VARIABLES.map(({ v, note }) => (
        <Tooltip key={v} title={`Available for: ${note}`} placement="top">
          <Chip
            label={`{{${v}}}`}
            size="small"
            onClick={() => onInsert(`{{${v}}}`)}
            sx={[{ cursor: "pointer", fontFamily: "monospace" }, phoneChipSx(28)]}
          />
        </Tooltip>
      ))}
    </MDBox>
  );
}

// ─── Default empty step ───────────────────────────────────────────────────────
function newStep(order) {
  return {
    order,
    name: `Step ${order}`,
    active: true,
    delayValue: 0,
    delayUnit: "minutes",
    anchor: "registration",
    offsetHours: 0,
    email: { active: false, subject: "", htmlContent: "", attachments: [] },
    whatsapp: { active: false, textContent: "" },
    sms: { active: false, textContent: "" },
  };
}

// ─── Insert text at textarea cursor ──────────────────────────────────────────
function insertAtCursor(setter, ref, text) {
  const el = ref.current?.querySelector("textarea");
  if (!el) {
    setter((prev) => prev + text);
    return;
  }
  const start = el.selectionStart;
  const end = el.selectionEnd;
  const value = el.value;
  setter(value.slice(0, start) + text + value.slice(end));
  requestAnimationFrame(() => {
    el.selectionStart = el.selectionEnd = start + text.length;
    el.focus();
  });
}

// ─── Single Step Editor ───────────────────────────────────────────────────────
function StepEditor({ step, index, campaignId, onChange, onDelete, onAttachmentsChanged }) {
  const [tab, setTab] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [attachError, setAttachError] = useState("");
  const waRef = useRef(null);
  const smsRef = useRef(null);
  const subjectRef = useRef(null);
  const bodyRef = useRef(null);
  // The Email field the placeholder chips insert into: whichever was focused last (the body
  // until the Subject is clicked).
  const emailTarget = useRef("body");

  const set = (field, value) => onChange({ ...step, [field]: value });
  const setChannel = (ch, field, value) =>
    onChange({ ...step, [ch]: { ...step[ch], [field]: value } });

  const handleAttachmentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !campaignId || !step._id) return;
    setAttachError("");
    const tooLarge = attachmentSizeError(file);
    if (tooLarge) {
      setAttachError(tooLarge);
      e.target.value = "";
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    try {
      setUploading(true);
      const data = await adminApi.uploadStepAttachment(campaignId, step._id, fd);
      onAttachmentsChanged(step._id, data.campaign);
    } catch (err) {
      setAttachError(
        err.response?.data?.error || `${file.name} could not be uploaded: ${err.message}`
      );
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const handleAttachmentDelete = async (filename) => {
    if (!campaignId || !step._id) return;
    setAttachError("");
    try {
      const data = await adminApi.deleteStepAttachment(campaignId, step._id, filename);
      onAttachmentsChanged(step._id, data.campaign);
    } catch (err) {
      setAttachError(err.response?.data?.error || `The file could not be removed: ${err.message}`);
    }
  };

  return (
    <Accordion defaultExpanded={index === 0} disableGutters>
      <AccordionSummary expandIcon={<Icon>expand_more</Icon>}>
        <MDBox display="flex" alignItems="center" gap={1} width="100%">
          <Badge badgeContent={step.order} color="info" sx={{ mr: 1 }}>
            <Icon sx={{ color: step.active ? "success.main" : "text.disabled" }}>
              {step.active ? "radio_button_checked" : "radio_button_unchecked"}
            </Icon>
          </Badge>
          <MDTypography
            variant="button"
            fontWeight="medium"
            sx={{ flexGrow: 1, minWidth: 0, overflowWrap: "anywhere" }}
          >
            {step.name || `Step ${step.order}`}
          </MDTypography>
          <MDTypography
            variant="caption"
            color="secondary"
            sx={{ mr: { xs: 0, md: 2 }, whiteSpace: "nowrap" }}
          >
            {step.anchor === "webinar"
              ? `webinar ${step.offsetHours >= 0 ? "+" : ""}${step.offsetHours}h`
              : `${step.delayValue} ${step.delayUnit}`}
          </MDTypography>
          <Tooltip title="Delete step">
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              sx={actionTapSx}
            >
              <Icon fontSize="small">delete</Icon>
            </IconButton>
          </Tooltip>
        </MDBox>
      </AccordionSummary>

      {/* 12px sides on phones leave room for the three channel tabs at 360px (16px is MUI's default). */}
      <AccordionDetails sx={{ px: { xs: 1.5, md: 2 } }}>
        <Grid container spacing={2} mb={2}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              label="Step Name"
              fullWidth
              size="small"
              value={step.name}
              onChange={(e) => set("name", e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              label="Send relative to"
              fullWidth
              size="small"
              value={step.anchor || "registration"}
              onChange={(e) => set("anchor", e.target.value)}
            >
              <MenuItem value="registration">Registration time</MenuItem>
              <MenuItem value="webinar">Webinar start</MenuItem>
            </TextField>
          </Grid>
          {(step.anchor || "registration") === "webinar" ? (
            <Grid item xs={12} sm={8} md={4}>
              <TextField
                label="Offset (hours, negative = before)"
                type="number"
                fullWidth
                size="small"
                value={step.offsetHours ?? 0}
                onChange={(e) => set("offsetHours", Number(e.target.value))}
                helperText="Uses the course's Session Start (ISO 8601) setting"
              />
            </Grid>
          ) : (
            <>
              <Grid item xs={6} sm={4} md={2}>
                <TextField
                  label="Delay"
                  type="number"
                  fullWidth
                  size="small"
                  inputProps={{ min: 0 }}
                  value={step.delayValue}
                  onChange={(e) => set("delayValue", Number(e.target.value))}
                />
              </Grid>
              <Grid item xs={6} sm={4} md={2}>
                <TextField
                  select
                  label="Unit"
                  fullWidth
                  size="small"
                  value={step.delayUnit}
                  onChange={(e) => set("delayUnit", e.target.value)}
                >
                  {["minutes", "hours", "days"].map((u) => (
                    <MenuItem key={u} value={u}>
                      {u}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </>
          )}
          <Grid item xs={12} sm={4} md={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={step.active}
                  onChange={(e) => set("active", e.target.checked)}
                  size="small"
                />
              }
              label="Active"
            />
          </Grid>
        </Grid>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="Email" icon={<Icon>email</Icon>} iconPosition="start" />
          <Tab label="WhatsApp" icon={<WhatsAppIcon fontSize="small" />} iconPosition="start" />
          <Tab label="SMS" icon={<Icon>sms</Icon>} iconPosition="start" />
        </Tabs>

        {/* ── Email Tab ── */}
        {tab === 0 && (
          <MDBox>
            <FormControlLabel
              control={
                <Switch
                  checked={step.email.active}
                  onChange={(e) => setChannel("email", "active", e.target.checked)}
                  size="small"
                />
              }
              label="Enable email for this step"
              sx={{ mb: 1 }}
            />
            {step.email.active && (
              <>
                <VariableChips
                  onInsert={(v) => {
                    if (emailTarget.current === "body") {
                      insertIntoRichText(
                        bodyRef.current,
                        v,
                        (html) => setChannel("email", "htmlContent", html),
                        (el) => Quill.find(el),
                        step.email.htmlContent
                      );
                      return;
                    }
                    const el = subjectRef.current?.querySelector("input");
                    if (!el) {
                      setChannel("email", "subject", step.email.subject + v);
                      return;
                    }
                    const s = el.selectionStart,
                      end = el.selectionEnd;
                    const next = step.email.subject.slice(0, s) + v + step.email.subject.slice(end);
                    setChannel("email", "subject", next);
                  }}
                />
                <MDTypography variant="caption" color="secondary" display="block" mb={1}>
                  Placeholders go into the Subject or the Body — whichever you clicked last.
                </MDTypography>
                <TextField
                  ref={subjectRef}
                  label="Subject"
                  fullWidth
                  size="small"
                  sx={{ mb: 2 }}
                  value={step.email.subject}
                  onFocus={() => {
                    emailTarget.current = "subject";
                  }}
                  onChange={(e) => setChannel("email", "subject", e.target.value)}
                />
                <MDTypography variant="caption" color="secondary" display="block" mb={0.5}>
                  Email Body (HTML — variables like {"{{name}}"} are supported)
                </MDTypography>
                <MDBox
                  ref={bodyRef}
                  onFocusCapture={() => {
                    emailTarget.current = "body";
                  }}
                >
                  <RichTextEditor
                    value={step.email.htmlContent}
                    onChange={(v) => setChannel("email", "htmlContent", v)}
                    label="Email content"
                    height={300}
                  />
                </MDBox>
                <MDBox mt={2}>
                  <MDTypography variant="button" fontWeight="medium">
                    Attachments
                  </MDTypography>
                  {(step.email.attachments || []).map((att) => (
                    <MDBox key={att.path} display="flex" alignItems="center" gap={1} mt={0.5}>
                      <Icon fontSize="small">attach_file</Icon>
                      <MDTypography
                        variant="caption"
                        sx={{ flexGrow: 1, minWidth: 0, overflowWrap: "anywhere" }}
                      >
                        {att.filename}
                      </MDTypography>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleAttachmentDelete(att.path)}
                        sx={actionTapSx}
                      >
                        <Icon fontSize="small">close</Icon>
                      </IconButton>
                    </MDBox>
                  ))}
                  <MDBox mt={1} display="flex" flexWrap="wrap" alignItems="center" gap={1}>
                    <label>
                      <input type="file" hidden onChange={handleAttachmentUpload} />
                      <MDButton
                        component="span"
                        variant="outlined"
                        color="info"
                        size="small"
                        startIcon={uploading ? <CircularProgress size={14} /> : <Icon>upload</Icon>}
                        disabled={uploading || !campaignId || !step._id}
                      >
                        {uploading ? "Uploading…" : "Attach File"}
                      </MDButton>
                    </label>
                    {(!campaignId || !step._id) && (
                      <MDTypography variant="caption" color="secondary">
                        Save campaign first to enable attachments
                      </MDTypography>
                    )}
                  </MDBox>
                  {attachError && (
                    <MDTypography
                      variant="caption"
                      color="error"
                      display="block"
                      mt={1}
                      role="alert"
                    >
                      {attachError}
                    </MDTypography>
                  )}
                </MDBox>
              </>
            )}
          </MDBox>
        )}

        {/* ── WhatsApp Tab ── */}
        {tab === 1 && (
          <MDBox>
            <FormControlLabel
              control={
                <Switch
                  checked={step.whatsapp.active}
                  onChange={(e) => setChannel("whatsapp", "active", e.target.checked)}
                  size="small"
                />
              }
              label="Enable WhatsApp for this step"
              sx={{ mb: 1 }}
            />
            {step.whatsapp.active && (
              <>
                <VariableChips
                  onInsert={(v) =>
                    insertAtCursor((val) => setChannel("whatsapp", "textContent", val), waRef, v)
                  }
                />
                <TextField
                  ref={waRef}
                  label="WhatsApp Message"
                  fullWidth
                  multiline
                  minRows={5}
                  value={step.whatsapp.textContent}
                  onChange={(e) => setChannel("whatsapp", "textContent", e.target.value)}
                  helperText="Plain text. Variables like {{name}} will be replaced with applicant data."
                />
              </>
            )}
          </MDBox>
        )}

        {/* ── SMS Tab ── */}
        {tab === 2 && (
          <MDBox>
            <FormControlLabel
              control={
                <Switch
                  checked={step.sms.active}
                  onChange={(e) => setChannel("sms", "active", e.target.checked)}
                  size="small"
                />
              }
              label="Enable SMS for this step"
              sx={{ mb: 1 }}
            />
            {step.sms.active && (
              <>
                <VariableChips
                  onInsert={(v) =>
                    insertAtCursor((val) => setChannel("sms", "textContent", val), smsRef, v)
                  }
                />
                <TextField
                  ref={smsRef}
                  label="SMS Message"
                  fullWidth
                  multiline
                  minRows={4}
                  value={step.sms.textContent}
                  onChange={(e) => setChannel("sms", "textContent", e.target.value)}
                  helperText={`${step.sms.textContent.length} chars — standard SMS is 160 chars (multi-part sent automatically)`}
                />
              </>
            )}
          </MDBox>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

// ─── Campaign Editor Dialog ───────────────────────────────────────────────────
function CampaignEditor({ open, campaign, onClose, onSaved }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [active, setActive] = useState(true);
  const [expiresAt, setExpiresAt] = useState("");
  const [sources, setSources] = useState(["landing_application"]);
  const [courseSlugs, setCourseSlugs] = useState([]);
  const [audience, setAudience] = useState(EMPTY_AUDIENCE);
  const [steps, setSteps] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  // savedCampaign holds the last-persisted version (needed for attachment endpoints)
  const [savedCampaign, setSavedCampaign] = useState(null);
  // Highest step number shown in this editor session — a removed step's number is never reused.
  const highestOrderRef = useRef(0);

  useEffect(() => {
    if (campaign) {
      setName(campaign.name || "");
      setDescription(campaign.description || "");
      setActive(campaign.active !== false);
      // Date input wants YYYY-MM-DD — expiresAt comes back as a full ISO datetime.
      setExpiresAt(campaign.expiresAt ? campaign.expiresAt.slice(0, 10) : "");
      setSources(campaign.sources?.length ? campaign.sources : ["landing_application"]);
      setCourseSlugs(campaign.courseSlugs || []);
      setAudience({ ...EMPTY_AUDIENCE, ...(campaign.audience || {}) });
      setSteps(campaign.steps || []);
      highestOrderRef.current = nextStepOrder(campaign.steps || []) - 1;
      setSavedCampaign(campaign);
    } else {
      setName("");
      setDescription("");
      setActive(true);
      setExpiresAt("");
      setSources(["landing_application"]);
      setCourseSlugs([]);
      setAudience(EMPTY_AUDIENCE);
      setSteps([newStep(1)]);
      highestOrderRef.current = 1;
      setSavedCampaign(null);
    }
    setError("");
  }, [campaign, open]);

  const toggleSource = (value) => {
    setSources((prev) =>
      prev.includes(value)
        ? prev.length > 1
          ? prev.filter((s) => s !== value)
          : prev // keep at least one
        : [...prev, value]
    );
  };

  const toggleCourse = (value) => {
    setCourseSlugs((prev) =>
      prev.includes(value) ? prev.filter((c) => c !== value) : [...prev, value]
    );
  };

  const addStep = () => {
    const order = nextStepOrder(steps, highestOrderRef.current);
    highestOrderRef.current = order;
    setSteps((prev) => [...prev, newStep(order)]);
  };

  const updateStep = (idx, updated) => {
    setSteps((prev) => prev.map((s, i) => (i === idx ? updated : s)));
  };

  // No renumbering: every step keeps its number (see ./steps).
  const deleteStep = (idx) => {
    setSteps((prev) => removeStep(prev, idx));
  };

  // Called after an attachment upload/removal (both save straight to the server): take that
  // step's attachment list from the saved campaign but keep every unsaved edit, and refresh the
  // list page's copy so it never holds (and later re-saves) a stale attachment list.
  const syncAttachments = (stepId, updatedCampaign) => {
    setSavedCampaign(updatedCampaign);
    setSteps((prev) => mergeStepAttachments(prev, updatedCampaign, stepId));
    onSaved(updatedCampaign);
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Campaign name is required");
      return;
    }
    try {
      setSaving(true);
      setError("");
      const payload = {
        name,
        description,
        active,
        sources,
        courseSlugs,
        audience,
        steps,
        expiresAt: expiresAt ? new Date(`${expiresAt}T23:59:59`).toISOString() : null,
      };
      let result;
      if (savedCampaign?._id) {
        result = await adminApi.updateCampaign(savedCampaign._id, payload);
      } else {
        result = await adminApi.createCampaign(payload);
      }
      setSavedCampaign(result.campaign);
      setSteps(result.campaign.steps || []);
      highestOrderRef.current =
        nextStepOrder(result.campaign.steps || [], highestOrderRef.current) - 1;
      onSaved(result.campaign);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save campaign");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth fullScreen={fullScreen}>
      <DialogTitle>{savedCampaign ? "Edit Campaign" : "New Campaign"}</DialogTitle>
      <DialogContent dividers>
        {error && (
          <MDBox mb={2}>
            <MDAlert color="error" dismissible onClose={() => setError("")}>
              {error}
            </MDAlert>
          </MDBox>
        )}

        <Grid container spacing={2} mb={2}>
          <Grid item xs={12} sm={6} md={5}>
            <TextField
              label="Campaign Name"
              fullWidth
              size="small"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              label="Description (optional)"
              fullWidth
              size="small"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              label="Expires On (optional)"
              type="date"
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              helperText={expiresAt ? "No sends after this date" : "Never expires"}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={1} display="flex" alignItems="center">
            <FormControlLabel
              control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} />}
              label="On"
            />
          </Grid>
        </Grid>

        <MDBox mb={3} p={2} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
          <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
            Applies to
          </MDTypography>
          <FormGroup row>
            {SOURCES.map((src) => (
              <FormControlLabel
                key={src.value}
                control={
                  <Checkbox
                    checked={sources.includes(src.value)}
                    onChange={() => toggleSource(src.value)}
                    size="small"
                    color={src.color}
                  />
                }
                label={
                  <MDBox display="flex" alignItems="center" gap={0.5}>
                    <Icon
                      fontSize="small"
                      sx={{ color: `${src.color}.main`, verticalAlign: "middle" }}
                    >
                      {src.icon}
                    </Icon>
                    <MDTypography variant="caption">{src.label}</MDTypography>
                  </MDBox>
                }
              />
            ))}
          </FormGroup>
          <MDTypography variant="caption" color="secondary">
            This campaign will run for every contact from the selected sources. At least one source
            is required.
          </MDTypography>
        </MDBox>

        <MDBox mb={3} p={2} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
          <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
            Courses
          </MDTypography>
          <FormGroup row>
            {COURSES.map((course) => (
              <FormControlLabel
                key={course.value}
                control={
                  <Checkbox
                    checked={courseSlugs.includes(course.value)}
                    onChange={() => toggleCourse(course.value)}
                    size="small"
                  />
                }
                label={<MDTypography variant="caption">{course.label}</MDTypography>}
              />
            ))}
          </FormGroup>
          <MDTypography variant="caption" color="secondary">
            Restrict this campaign to specific courses. Leave all unchecked to apply regardless of
            course (backward-compatible default).
          </MDTypography>
        </MDBox>

        <MDBox mb={3} p={2} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
          <TextField
            label="Only contacts who signed up on/after"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={audience.registeredAfter ? audience.registeredAfter.slice(0, 10) : ""}
            onChange={(e) => {
              const value = e.target.value;
              setAudience((a) => ({
                ...a,
                registeredAfter: value ? new Date(`${value}T00:00:00Z`).toISOString() : null,
              }));
            }}
            helperText="Leave empty to include everyone. Use this when switching a campaign on so it does not email every historical contact at once."
            sx={{ minWidth: { xs: "100%", sm: 260 } }}
          />
        </MDBox>

        {sources.includes("webinar_registration") && (
          <MDBox mb={3} p={2} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
            <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
              Audience (webinar registrants)
            </MDTypography>
            <FormGroup row>
              {[
                "registered",
                "contacted",
                "enrolled",
                "rejected",
                "pending",
                "attended",
                "missed",
              ].map((stage) => (
                <FormControlLabel
                  key={stage}
                  control={
                    <Checkbox
                      size="small"
                      checked={audience.registrationStages.includes(stage)}
                      onChange={() =>
                        setAudience((a) => ({
                          ...a,
                          registrationStages: a.registrationStages.includes(stage)
                            ? a.registrationStages.filter((s) => s !== stage)
                            : [...a.registrationStages, stage],
                        }))
                      }
                    />
                  }
                  label={<MDTypography variant="caption">{stage}</MDTypography>}
                />
              ))}
            </FormGroup>
            <TextField
              select
              size="small"
              label="Clarity call booked"
              sx={{ mt: 1, minWidth: { xs: "100%", sm: 220 } }}
              value={audience.callBooked}
              onChange={(e) => setAudience((a) => ({ ...a, callBooked: e.target.value }))}
            >
              <MenuItem value="any">Any</MenuItem>
              <MenuItem value="only">Only registrants who booked a call</MenuItem>
              <MenuItem value="exclude">Exclude registrants who booked a call</MenuItem>
            </TextField>
            <MDTypography variant="caption" color="secondary" display="block" mt={1}>
              Leave every stage unchecked to include all stages. Tag registrants "attended" /
              "missed" on the Webinar Registrations page after the session.
            </MDTypography>
          </MDBox>
        )}

        {sources.includes("clarity_call_booking") && (
          <MDBox mb={3} p={2} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
            <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
              Audience (Clarity Call bookings)
            </MDTypography>
            <FormGroup row>
              {BOOKING_STATUS_OPTIONS.map((status) => (
                <FormControlLabel
                  key={status}
                  control={
                    <Checkbox
                      size="small"
                      checked={audience.bookingStatuses.includes(status)}
                      onChange={() =>
                        setAudience((a) => ({
                          ...a,
                          bookingStatuses: a.bookingStatuses.includes(status)
                            ? a.bookingStatuses.filter((s) => s !== status)
                            : [...a.bookingStatuses, status],
                        }))
                      }
                    />
                  }
                  label={
                    <MDTypography variant="caption">
                      {status.toLowerCase().replace("_", " ")}
                    </MDTypography>
                  }
                />
              ))}
            </FormGroup>
            <MDTypography variant="caption" color="secondary" display="block" mt={1}>
              Leave every status unchecked to reach every booking except cancelled ones. Tick
              &quot;cancelled&quot; for a win-back campaign.
            </MDTypography>
          </MDBox>
        )}

        <MDBox
          display="flex"
          flexWrap="wrap"
          gap={1.5}
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <MDTypography variant="button" fontWeight="bold">
            Sequence Steps ({steps.length})
          </MDTypography>
          <MDButton
            size="small"
            variant="outlined"
            color="info"
            startIcon={<Icon>add</Icon>}
            onClick={addStep}
          >
            Add Step
          </MDButton>
        </MDBox>

        {steps.length === 0 && (
          <MDBox p={3} textAlign="center">
            <MDTypography variant="body2" color="secondary">
              No steps yet — click "Add Step" to build your sequence.
            </MDTypography>
          </MDBox>
        )}

        {steps.map((step, idx) => (
          <StepEditor
            key={step._id || `new-${step.order}`}
            step={step}
            index={idx}
            campaignId={savedCampaign?._id}
            onChange={(updated) => updateStep(idx, updated)}
            onDelete={() => deleteStep(idx)}
            onAttachmentsChanged={syncAttachments}
          />
        ))}
      </DialogContent>
      <DialogActions>
        <MDButton onClick={onClose} color="secondary" disabled={saving}>
          Close
        </MDButton>
        <MDButton
          onClick={handleSave}
          color="success"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Icon>save</Icon>}
        >
          {saving ? "Saving…" : savedCampaign ? "Save Changes" : "Create Campaign"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

// ─── Logs Dialog ─────────────────────────────────────────────────────────────
function LogsDialog({ open, campaign, onClose }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !campaign) return;
    setLoading(true);
    adminApi
      .getCampaignLogs({ campaignId: campaign._id })
      .then((d) => setLogs(d.logs || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open, campaign]);

  const channelIcon = { email: "email", sms: "sms" };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen}>
      <DialogTitle sx={{ overflowWrap: "anywhere" }}>Send Log — {campaign?.name}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <MDBox textAlign="center" py={3}>
            <CircularProgress />
          </MDBox>
        ) : logs.length === 0 ? (
          <MDTypography variant="body2" color="secondary" textAlign="center" py={3}>
            No messages sent yet for this campaign.
          </MDTypography>
        ) : (
          logs.map((log, i) => {
            const src = SOURCE_MAP[log.source];
            return (
              // Phones: icons + recipient on the first line, chips + time wrap onto the next.
              <MDBox
                key={i}
                display="flex"
                alignItems="center"
                gap={1}
                flexWrap={{ xs: "wrap", md: "nowrap" }}
                py={1}
                sx={{ borderBottom: "1px solid", borderColor: "divider" }}
              >
                <Icon
                  fontSize="small"
                  sx={{ color: log.status === "sent" ? "success.main" : "error.main" }}
                >
                  {log.status === "sent" ? "check_circle" : "error"}
                </Icon>
                {log.channel === "whatsapp" ? (
                  <WhatsAppIcon fontSize="small" />
                ) : (
                  <Icon fontSize="small">{channelIcon[log.channel] || "send"}</Icon>
                )}
                <MDTypography
                  variant="caption"
                  sx={{
                    flexGrow: 1,
                    flexBasis: { xs: "calc(100% - 64px)", md: "auto" },
                    minWidth: 0,
                    overflowWrap: "anywhere",
                  }}
                >
                  {log.recipientEmail || log.applicantEmail || log.recordId || log.applicationId}
                </MDTypography>
                {src && (
                  <Chip
                    icon={<Icon style={{ fontSize: 11 }}>{src.icon}</Icon>}
                    label={src.label.split(" ")[0]}
                    size="small"
                    color={src.color}
                    variant="outlined"
                    sx={{ minWidth: 80 }}
                  />
                )}
                <Chip
                  label={log.channel}
                  size="small"
                  color={log.status === "sent" ? "success" : "error"}
                  sx={{ minWidth: 70 }}
                />
                <MDTypography variant="caption" color="secondary">
                  {log.sentAt ? new Date(log.sentAt).toLocaleString() : "—"}
                </MDTypography>
                {log.error && (
                  <Tooltip title={log.error} enterTouchDelay={0}>
                    <Icon fontSize="small" color="error">
                      info
                    </Icon>
                  </Tooltip>
                )}
              </MDBox>
            );
          })
        )}
      </DialogContent>
      <DialogActions>
        <MDButton onClick={onClose} color="secondary">
          Close
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
function Autoresponder() {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("md"));
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);

  const [logsOpen, setLogsOpen] = useState(false);
  const [logsCampaign, setLogsCampaign] = useState(null);

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getCampaigns();
      setCampaigns(data.campaigns || []);
      setError("");
    } catch {
      setError("Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  };

  const handleSaved = (campaign) => {
    setCampaigns((prev) => {
      const idx = prev.findIndex((c) => c._id === campaign._id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = campaign;
        return next;
      }
      return [campaign, ...prev];
    });
    // keep editor open so user can continue editing / add attachments
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await adminApi.deleteCampaign(deleteTarget._id);
      setCampaigns((prev) => prev.filter((c) => c._id !== deleteTarget._id));
      setDeleteTarget(null);
    } catch {
      setError("Failed to delete campaign");
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActive = async (campaign) => {
    try {
      const updated = await adminApi.updateCampaign(campaign._id, {
        ...campaign,
        active: !campaign.active,
      });
      setCampaigns((prev) => prev.map((c) => (c._id === campaign._id ? updated.campaign : c)));
    } catch {
      setError("Failed to update campaign status");
    }
  };

  const columns = [
    { Header: "campaign", accessor: "campaign", width: "22%", align: "left" },
    { Header: "sources", accessor: "sources", width: "20%", align: "left" },
    { Header: "courses", accessor: "courses", width: "15%", align: "left" },
    { Header: "steps", accessor: "steps", width: "7%", align: "center" },
    { Header: "active", accessor: "active", width: "7%", align: "center" },
    { Header: "created", accessor: "created", width: "12%", align: "left" },
    { Header: "actions", accessor: "actions", width: "17%", align: "center" },
  ];

  const rows = campaigns.map((c) => ({
    // Not a column — rendered by renderRowDetail as a full-width line under the row.
    description: c.description,
    campaign: (
      // maxWidth lets long names wrap — DataTable cells otherwise size to max-content.
      // On phones the card layout sets the width instead, so the cap only applies from md up.
      <MDBox maxWidth={{ xs: "100%", md: 220 }} whiteSpace="normal">
        <MDTypography variant="button" fontWeight="medium" display="block">
          {c.name}
        </MDTypography>
        {c.expiresAt && (
          <Chip
            icon={<Icon style={{ fontSize: 12 }}>event_busy</Icon>}
            label={`Expires ${new Date(c.expiresAt).toLocaleDateString()}`}
            size="small"
            color={new Date(c.expiresAt).getTime() < Date.now() ? "error" : "default"}
            variant="outlined"
            sx={[{ mt: 0.5 }, phoneChipSx(24)]}
          />
        )}
      </MDBox>
    ),
    sources: (
      <MDBox display="flex" flexWrap="wrap" gap={0.5}>
        {(c.sources?.length ? c.sources : ["landing_application"]).map((s) => {
          const src = SOURCE_MAP[s];
          return src ? (
            <Chip
              key={s}
              icon={<Icon style={{ fontSize: 12 }}>{src.icon}</Icon>}
              label={src.label.split(" ")[0]}
              size="small"
              color={src.color}
              variant="outlined"
              sx={phoneChipSx(24)}
            />
          ) : null;
        })}
      </MDBox>
    ),
    courses: (
      <MDBox display="flex" flexWrap="wrap" gap={0.5}>
        {c.courseSlugs?.length ? (
          c.courseSlugs.map((slug) => {
            const course = COURSE_MAP[slug];
            return (
              <Chip
                key={slug}
                label={course ? course.label : slug}
                size="small"
                color="secondary"
                variant="outlined"
                sx={phoneChipSx(24)}
              />
            );
          })
        ) : (
          <MDTypography variant="caption" color="secondary">
            All courses
          </MDTypography>
        )}
      </MDBox>
    ),
    steps: (
      <Chip
        label={`${(c.steps || []).filter((s) => s.active).length} / ${(c.steps || []).length}`}
        size="small"
        color="info"
      />
    ),
    active: (
      <Switch
        checked={c.active}
        onChange={() => handleToggleActive(c)}
        // medium on phones for a 38px tap target (the MD theme keeps the track the same size)
        size={isPhone ? "medium" : "small"}
        color="success"
      />
    ),
    created: (
      <MDTypography variant="caption">
        {c.createdAt ? new Date(c.createdAt).toLocaleDateString() : "—"}
      </MDTypography>
    ),
    actions: (
      <MDBox display="flex" gap={{ xs: 1, md: 0.5 }} justifyContent="center">
        <Tooltip title="Edit campaign">
          <IconButton
            size="small"
            onClick={() => {
              setEditingCampaign(c);
              setEditorOpen(true);
            }}
            sx={actionTapSx}
          >
            <Icon fontSize="small">edit</Icon>
          </IconButton>
        </Tooltip>
        <Tooltip title="View send log">
          <IconButton
            size="small"
            color="info"
            sx={actionTapSx}
            onClick={() => {
              setLogsCampaign(c);
              setLogsOpen(true);
            }}
          >
            <Icon fontSize="small">history</Icon>
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete campaign">
          <IconButton
            size="small"
            color="error"
            onClick={() => setDeleteTarget(c)}
            sx={actionTapSx}
          >
            <Icon fontSize="small">delete</Icon>
          </IconButton>
        </Tooltip>
      </MDBox>
    ),
  }));

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
                display="flex"
                flexWrap="wrap"
                gap={1.5}
                justifyContent="space-between"
                alignItems="center"
              >
                <MDTypography variant="h6" color="white">
                  Autoresponder Campaigns
                </MDTypography>
                <MDButton
                  variant="outlined"
                  color="white"
                  size="small"
                  startIcon={<Icon>add</Icon>}
                  onClick={() => {
                    setEditingCampaign(null);
                    setEditorOpen(true);
                  }}
                >
                  New Campaign
                </MDButton>
              </MDBox>

              {error && (
                <MDBox pt={2} px={2}>
                  <MDAlert color="error" dismissible onClose={() => setError("")}>
                    {error}
                  </MDAlert>
                </MDBox>
              )}

              <MDBox pt={3}>
                {loading ? (
                  <MDBox p={3} textAlign="center">
                    <MDTypography variant="body2">Loading campaigns...</MDTypography>
                  </MDBox>
                ) : campaigns.length === 0 ? (
                  <MDBox p={3} textAlign="center">
                    <MDTypography variant="body2" color="secondary">
                      No campaigns yet. Click "New Campaign" to get started.
                    </MDTypography>
                  </MDBox>
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                    renderRowDetail={(row) =>
                      row.description ? (
                        <MDTypography variant="caption" color="secondary" display="block">
                          {row.description}
                        </MDTypography>
                      ) : null
                    }
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Campaign Editor */}
      <CampaignEditor
        open={editorOpen}
        campaign={editingCampaign}
        onClose={() => setEditorOpen(false)}
        onSaved={handleSaved}
      />

      {/* Logs Dialog */}
      <LogsDialog open={logsOpen} campaign={logsCampaign} onClose={() => setLogsOpen(false)} />

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Campaign</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2">
            Delete <strong>{deleteTarget?.name}</strong>? This cannot be undone. Send logs will be
            retained.
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton onClick={() => setDeleteTarget(null)} color="secondary" disabled={deleting}>
            Cancel
          </MDButton>
          <MDButton onClick={handleDelete} color="error" disabled={deleting}>
            {deleting ? "Deleting…" : "Delete"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default Autoresponder;
